import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Download,
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
  BedDouble,
  Calendar,
  FileText,
  BarChart3,
  Target,
  Printer,
  FileSpreadsheet,
  GitCompare,
  ArrowUpRight,
  ArrowDownRight,
  ClipboardCheck,
  Eye,
  Table,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#0F172A", "#B45309", "#059669", "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981"];

// Full Preview Component for P&L and Cash Flow
const FullPreview = ({ currentYearData, property, formatCurrency, formatCurrencyPrecise }) => {
  const months = currentYearData?.months || [];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  // Calculate totals
  const annualRevenue = months.reduce((sum, m) => sum + (m.total_revenue || 0), 0);
  const annualExpenses = months.reduce((sum, m) => sum + (m.total_expenses || 0), 0);
  const annualProfit = annualRevenue - annualExpenses;
  
  // Group expenses by category
  const expensesByCategory = {};
  months.forEach(m => {
    if (m.expenses_by_category) {
      Object.entries(m.expenses_by_category).forEach(([cat, amount]) => {
        if (!expensesByCategory[cat]) expensesByCategory[cat] = Array(12).fill(0);
        expensesByCategory[cat][months.indexOf(m)] = amount;
      });
    }
  });

  return (
    <div className="space-y-8">
      {/* P&L Preview */}
      <Card className="border-slate-200">
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            Profit & Loss Statement - FY{currentYearData?.year}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-700 sticky left-0 bg-slate-100 min-w-[180px]">Category</th>
                  {monthNames.map(m => (
                    <th key={m} className="text-right p-3 font-semibold text-slate-700 min-w-[85px]">{m}</th>
                  ))}
                  <th className="text-right p-3 font-semibold text-slate-700 bg-slate-200 min-w-[100px]">Annual</th>
                </tr>
              </thead>
              <tbody>
                {/* Revenue Section */}
                <tr className="bg-emerald-50 font-semibold">
                  <td className="p-3 text-emerald-800 sticky left-0 bg-emerald-50">REVENUE</td>
                  <td colSpan={13}></td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Occupancy Rate</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{m.revenue?.occupancy_rate?.toFixed(0)}%</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    {(months.reduce((s, m) => s + (m.revenue?.occupancy_rate || 0), 0) / 12).toFixed(0)}%
                  </td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Average Daily Rate <span className="text-xs text-slate-400">(incl. GST)</span></td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">${m.revenue?.adr?.toFixed(0)}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    ${(months.reduce((s, m) => s + (m.revenue?.adr || 0), 0) / 12).toFixed(0)}
                  </td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Rooms Sold</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{m.revenue?.rooms_sold?.toLocaleString()}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    {months.reduce((s, m) => s + (m.revenue?.rooms_sold || 0), 0).toLocaleString()}
                  </td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Accommodation Revenue</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{formatCurrency(m.revenue?.accommodation_revenue || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    {formatCurrency(months.reduce((s, m) => s + (m.revenue?.accommodation_revenue || 0), 0))}
                  </td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Other Income</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{formatCurrency(m.revenue?.other_income || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    {formatCurrency(months.reduce((s, m) => s + (m.revenue?.other_income || 0), 0))}
                  </td>
                </tr>
                <tr className="bg-emerald-100 font-semibold border-b-2 border-emerald-300">
                  <td className="p-3 text-emerald-800 sticky left-0 bg-emerald-100">TOTAL REVENUE</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-emerald-800">{formatCurrency(m.total_revenue || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-emerald-200 text-emerald-900">{formatCurrency(annualRevenue)}</td>
                </tr>

                {/* Expenses Section */}
                <tr className="bg-rose-50 font-semibold">
                  <td className="p-3 text-rose-800 sticky left-0 bg-rose-50">OPERATING EXPENSES</td>
                  <td colSpan={13}></td>
                </tr>
                {Object.entries(expensesByCategory).map(([category, monthlyAmounts]) => (
                  <tr key={category} className="border-b hover:bg-slate-50">
                    <td className="p-3 pl-6 sticky left-0 bg-white text-slate-700">{category}</td>
                    {monthlyAmounts.map((amount, i) => (
                      <td key={i} className="text-right p-3 text-slate-600">{formatCurrency(amount)}</td>
                    ))}
                    <td className="text-right p-3 bg-slate-50 font-medium">
                      {formatCurrency(monthlyAmounts.reduce((s, a) => s + a, 0))}
                    </td>
                  </tr>
                ))}
                <tr className="bg-rose-100 font-semibold border-b-2 border-rose-300">
                  <td className="p-3 text-rose-800 sticky left-0 bg-rose-100">TOTAL EXPENSES</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-rose-800">{formatCurrency(m.total_expenses || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-rose-200 text-rose-900">{formatCurrency(annualExpenses)}</td>
                </tr>

                {/* Profit Section */}
                <tr className={`font-bold text-lg ${annualProfit >= 0 ? 'bg-emerald-200' : 'bg-rose-200'}`}>
                  <td className={`p-4 sticky left-0 ${annualProfit >= 0 ? 'bg-emerald-200 text-emerald-900' : 'bg-rose-200 text-rose-900'}`}>
                    NET OPERATING PROFIT
                  </td>
                  {months.map((m, i) => (
                    <td key={i} className={`text-right p-4 ${(m.operating_profit || 0) >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                      {formatCurrency(m.operating_profit || 0)}
                    </td>
                  ))}
                  <td className={`text-right p-4 ${annualProfit >= 0 ? 'bg-emerald-300 text-emerald-900' : 'bg-rose-300 text-rose-900'}`}>
                    {formatCurrency(annualProfit)}
                  </td>
                </tr>
                <tr>
                  <td className="p-3 pl-6 text-slate-600 sticky left-0 bg-white">Profit Margin</td>
                  {months.map((m, i) => {
                    const margin = m.total_revenue > 0 ? ((m.operating_profit || 0) / m.total_revenue * 100) : 0;
                    return (
                      <td key={i} className={`text-right p-3 ${margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {margin.toFixed(1)}%
                      </td>
                    );
                  })}
                  <td className={`text-right p-3 bg-slate-50 font-medium ${annualProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {annualRevenue > 0 ? (annualProfit / annualRevenue * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Cash Flow Preview */}
      <Card className="border-slate-200">
        <CardHeader className="bg-slate-50 border-b">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-slate-600" />
            Cash Flow Statement - FY{currentYearData?.year}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-700 sticky left-0 bg-slate-100 min-w-[180px]">Category</th>
                  {monthNames.map(m => (
                    <th key={m} className="text-right p-3 font-semibold text-slate-700 min-w-[85px]">{m}</th>
                  ))}
                  <th className="text-right p-3 font-semibold text-slate-700 bg-slate-200 min-w-[100px]">Annual</th>
                </tr>
              </thead>
              <tbody>
                {/* Inflows */}
                <tr className="bg-emerald-50 font-semibold">
                  <td className="p-3 text-emerald-800 sticky left-0 bg-emerald-50">CASH INFLOWS</td>
                  <td colSpan={13}></td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Operating Revenue</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{formatCurrency(m.cash_flow?.operating_revenue || m.total_revenue || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    {formatCurrency(months.reduce((s, m) => s + (m.cash_flow?.operating_revenue || m.total_revenue || 0), 0))}
                  </td>
                </tr>
                <tr className="bg-emerald-100 font-semibold">
                  <td className="p-3 text-emerald-800 sticky left-0 bg-emerald-100">Total Inflows</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-emerald-800">{formatCurrency(m.cash_flow?.total_inflow || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-emerald-200">
                    {formatCurrency(months.reduce((s, m) => s + (m.cash_flow?.total_inflow || 0), 0))}
                  </td>
                </tr>

                {/* Outflows */}
                <tr className="bg-rose-50 font-semibold">
                  <td className="p-3 text-rose-800 sticky left-0 bg-rose-50">CASH OUTFLOWS</td>
                  <td colSpan={13}></td>
                </tr>
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Operating Expenses</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{formatCurrency(m.cash_flow?.purchases || m.total_expenses || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50 font-medium">
                    {formatCurrency(months.reduce((s, m) => s + (m.cash_flow?.purchases || m.total_expenses || 0), 0))}
                  </td>
                </tr>
                <tr className="bg-rose-100 font-semibold">
                  <td className="p-3 text-rose-800 sticky left-0 bg-rose-100">Total Outflows</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-rose-800">{formatCurrencyPrecise(m.cash_flow?.total_outflow || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-rose-200">
                    {formatCurrencyPrecise(months.reduce((s, m) => s + (m.cash_flow?.total_outflow || 0), 0))}
                  </td>
                </tr>

                {/* Net Movement */}
                <tr className="bg-blue-100 font-semibold">
                  <td className="p-3 text-blue-800 sticky left-0 bg-blue-100">NET CASH MOVEMENT</td>
                  {months.map((m, i) => {
                    const net = m.cash_flow?.net_movement || 0;
                    return (
                      <td key={i} className={`text-right p-3 ${net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatCurrency(net)}
                      </td>
                    );
                  })}
                  <td className="text-right p-3 bg-blue-200">
                    {formatCurrency(months.reduce((s, m) => s + (m.cash_flow?.net_movement || 0), 0))}
                  </td>
                </tr>

                {/* Bank Balance */}
                <tr className="border-b hover:bg-slate-50">
                  <td className="p-3 pl-6 sticky left-0 bg-white">Opening Balance</td>
                  {months.map((m, i) => (
                    <td key={i} className="text-right p-3 text-slate-600">{formatCurrency(m.cash_flow?.opening_balance || 0)}</td>
                  ))}
                  <td className="text-right p-3 bg-slate-50">-</td>
                </tr>
                <tr className="bg-slate-200 font-bold">
                  <td className="p-3 text-slate-800 sticky left-0 bg-slate-200">CLOSING BALANCE</td>
                  {months.map((m, i) => {
                    const closing = m.cash_flow?.closing_balance || 0;
                    return (
                      <td key={i} className={`text-right p-3 ${closing >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatCurrencyPrecise(closing)}
                      </td>
                    );
                  })}
                  <td className="text-right p-3 bg-slate-300">
                    {formatCurrencyPrecise(months[months.length - 1]?.cash_flow?.closing_balance || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Helpful Note */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800">Preview Mode</p>
              <p className="text-sm text-blue-700 mt-1">
                This is a preview of your budget reports. Review the figures above - if you need to make changes, 
                go back to the <Link to={`/budget/${property?.id}`} className="underline font-medium">Budget Configuration</Link> page.
                Once satisfied, use "Download Excel" to get the complete 12-sheet workbook with all 5 years.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function ReportsPage() {
  const { budgetId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [scenarioData, setScenarioData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [viewMode, setViewMode] = useState("charts"); // "charts" or "preview"

  useEffect(() => {
    fetchProjections();
    fetchScenarios();
  }, [budgetId]);

  const fetchProjections = async () => {
    try {
      const response = await axios.get(`${API}/budgets/${budgetId}/projections`, {
        headers: getAuthHeader(),
      });
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load projections");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchScenarios = async () => {
    try {
      const response = await axios.get(`${API}/budgets/${budgetId}/scenarios`, {
        headers: getAuthHeader(),
      });
      setScenarioData(response.data);
    } catch (error) {
      console.error("Failed to load scenarios:", error);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/budgets/${budgetId}/export-excel`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${data?.property?.name || 'Budget'}_Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel report downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download Excel report");
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCurrencyPrecise = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatCompact = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner" />
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">No data available</p>
        </div>
      </Layout>
    );
  }

  const { property, projections } = data;
  const currentYearData = projections[selectedYear];
  const summary = currentYearData?.annual_summary;

  // Prepare chart data
  const monthlyChartData = currentYearData?.months.map((m) => ({
    name: m.month_name,
    revenue: m.total_revenue,
    expenses: m.total_expenses,
    profit: m.operating_profit,
    occupancy: m.revenue.occupancy_rate,
    adr: m.revenue.adr,
  }));

  const yearlyTrendData = projections.map((p) => ({
    year: `FY${p.year}`,
    revenue: p.annual_summary.total_revenue,
    profit: p.annual_summary.operating_profit,
    occupancy: p.annual_summary.average_occupancy,
  }));

  const expensePieData = Object.entries(summary?.expenses_by_category || {}).map(
    ([name, value]) => ({
      name,
      value,
    })
  );

  // Get expense details for tooltips
  const expenseDetails = summary?.expenses_details || {};

  const cashFlowData = currentYearData?.months.map((m) => ({
    name: m.month_name,
    inflow: m.cash_flow.total_inflow,
    outflow: m.cash_flow.total_outflow,
    net: m.cash_flow.net_movement,
  }));

  // Custom tooltip for expense breakdown
  const ExpenseTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const category = payload[0].name;
      const total = payload[0].value;
      const details = expenseDetails[category] || {};
      const sortedItems = Object.entries(details)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8); // Show top 8 items

      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 max-w-xs">
          <p className="font-semibold text-slate-900 border-b border-slate-100 pb-2 mb-2">
            {category}
          </p>
          <p className="text-sm font-medium text-emerald-600 mb-2">
            Total: {formatCurrency(total)}
          </p>
          {sortedItems.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Breakdown:</p>
              {sortedItems.map(([item, amount]) => (
                <div key={item} className="flex justify-between text-xs">
                  <span className="text-slate-600 truncate mr-2" style={{ maxWidth: '160px' }}>
                    {item}
                  </span>
                  <span className="font-mono text-slate-900 whitespace-nowrap">
                    {formatCurrency(amount)}
                  </span>
                </div>
              ))}
              {Object.keys(details).length > 8 && (
                <p className="text-xs text-slate-400 italic mt-1">
                  +{Object.keys(details).length - 8} more items
                </p>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-8 no-print" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <Link
              to={`/properties/${property.id}`}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Property
            </Link>
            <h1
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Financial Reports
            </h1>
            <p className="text-slate-500 mt-1">
              {property.name} • {property.total_rooms} rooms
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <Button
                variant={viewMode === "charts" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("charts")}
                className={`rounded-none ${viewMode === "charts" ? "bg-slate-900" : ""}`}
              >
                <BarChart3 className="w-4 h-4 mr-1" />
                Charts
              </Button>
              <Button
                variant={viewMode === "preview" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("preview")}
                className={`rounded-none ${viewMode === "preview" ? "bg-slate-900" : ""}`}
              >
                <Table className="w-4 h-4 mr-1" />
                Preview
              </Button>
            </div>
            
            <Select
              value={selectedYear.toString()}
              onValueChange={(v) => setSelectedYear(parseInt(v))}
            >
              <SelectTrigger className="w-36" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {projections.map((p, i) => (
                  <SelectItem key={i} value={i.toString()}>
                    FY {p.year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to={`/actuals/${budgetId}`}>
              <Button
                variant="outline"
                className="border-[#A5B89A] text-[#3C4C33] hover:bg-[#F3F5F2]"
                data-testid="actuals-btn"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Track Actuals
              </Button>
            </Link>
            <Button
              onClick={handleDownloadExcel}
              disabled={downloading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="download-excel-btn"
            >
              {downloading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner mr-2" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Download Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => window.print()}
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* Conditional Rendering based on View Mode */}
        {viewMode === "preview" ? (
          <FullPreview 
            currentYearData={currentYearData} 
            property={property} 
            formatCurrency={formatCurrency}
            formatCurrencyPrecise={formatCurrencyPrecise}
          />
        ) : (
          <>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-slate-200 stat-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Revenue</p>
                  <p
                    className="text-xl font-bold text-slate-900 mt-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {formatCurrency(summary?.total_revenue || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">FY {currentYearData?.year}</p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 stat-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Operating Profit</p>
                  <p
                    className={`text-xl font-bold mt-1 ${
                      (summary?.operating_profit || 0) >= 0 ? "text-emerald-600" : "text-rose-600"
                    }`}
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {formatCurrency(summary?.operating_profit || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {((summary?.operating_profit / summary?.total_revenue) * 100 || 0).toFixed(1)}% margin
                  </p>
                </div>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  (summary?.operating_profit || 0) >= 0 ? "bg-emerald-100" : "bg-rose-100"
                }`}>
                  {(summary?.operating_profit || 0) >= 0 ? (
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-rose-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 stat-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Avg Occupancy</p>
                  <p
                    className="text-xl font-bold text-blue-600 mt-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {summary?.average_occupancy || 0}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {summary?.total_rooms_sold?.toLocaleString() || 0} rooms sold
                  </p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Percent className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 stat-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">ARR</p>
                  <p
                    className="text-xl font-bold text-[#4B5E40] mt-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {formatCurrency(summary?.average_adr || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Average Room Rate
                  </p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-[#E8EDE5] flex items-center justify-center">
                  <BedDouble className="w-4 h-4 text-[#4B5E40]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 stat-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">RevPAR</p>
                  <p
                    className="text-xl font-bold text-amber-600 mt-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {formatCurrency(summary?.revpar || 0)}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Revenue per room
                  </p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="pnl" className="space-y-6">
          <TabsList className="bg-gradient-to-r from-emerald-600 to-teal-600 p-1.5 h-auto flex-wrap rounded-lg shadow-md">
            <TabsTrigger value="pnl" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              <FileText className="w-4 h-4 mr-2" />
              P&L Statement
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              <BarChart3 className="w-4 h-4 mr-2" />
              Cash Flow
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              <GitCompare className="w-4 h-4 mr-2" />
              Scenarios
            </TabsTrigger>
            <TabsTrigger value="breakeven" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              <Target className="w-4 h-4 mr-2" />
              Break-Even
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=inactive]:text-white data-[state=inactive]:hover:text-white/80 text-base sm:text-lg font-bold">
              <TrendingUp className="w-4 h-4 mr-2" />
              5-Year Trends
            </TabsTrigger>
          </TabsList>

          {/* P&L Statement Tab */}
          <TabsContent value="pnl" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Monthly Revenue Chart */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle>Monthly Revenue vs Expenses</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={monthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid #E2E8F0",
                          }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stackId="1"
                          stroke="#059669"
                          fill="#D1FAE5"
                          name="Revenue"
                        />
                        <Area
                          type="monotone"
                          dataKey="expenses"
                          stackId="2"
                          stroke="#E11D48"
                          fill="#FEE2E2"
                          name="Expenses"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Expense Breakdown */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Expense Breakdown</CardTitle>
                  <p className="text-xs text-slate-500">Hover over items to see detailed breakdown</p>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={expensePieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                        >
                          {expensePieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                              className="cursor-pointer hover:opacity-80 transition-opacity"
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<ExpenseTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 mt-4">
                    {expensePieData.slice(0, 5).map((item, index) => {
                      const details = expenseDetails[item.name] || {};
                      const itemCount = Object.keys(details).length;
                      return (
                        <div
                          key={item.name}
                          className="group relative flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50 rounded-lg p-1.5 -mx-1.5 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[index % COLORS.length] }}
                            />
                            <span className="text-slate-600 truncate max-w-[120px]">
                              {item.name}
                            </span>
                          </div>
                          <span className="font-mono text-slate-900">
                            {formatCompact(item.value)}
                          </span>
                          {/* Hover tooltip for expense items list */}
                          {itemCount > 0 && (
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                              <div className="bg-slate-900 text-white rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px]">
                                <p className="font-semibold text-sm border-b border-slate-700 pb-1 mb-2">
                                  {item.name}
                                </p>
                                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                  {Object.entries(details)
                                    .sort(([, a], [, b]) => b - a)
                                    .slice(0, 10)
                                    .map(([name, amount]) => (
                                      <div key={name} className="flex justify-between text-xs">
                                        <span className="text-slate-300 truncate mr-2" style={{ maxWidth: '150px' }}>
                                          {name}
                                        </span>
                                        <span className="text-emerald-400 whitespace-nowrap">
                                          {formatCurrency(amount)}
                                        </span>
                                      </div>
                                    ))}
                                  {itemCount > 10 && (
                                    <p className="text-xs text-slate-400 italic pt-1">
                                      +{itemCount - 10} more items
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly P&L Table */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Monthly Profit & Loss - FY {currentYearData?.year}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <div className="min-w-[800px]">
                    <table className="w-full budget-table text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 bg-slate-50">Month</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Rooms Sold</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Occupancy</th>
                          <th className="text-right py-3 px-4 bg-slate-50">ADR</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Revenue</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Expenses</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Profit</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentYearData?.months.map((month, index) => {
                          const margin = (month.operating_profit / month.total_revenue) * 100;
                          return (
                            <tr
                              key={index}
                              className="border-b border-slate-100 hover:bg-slate-50"
                            >
                              <td className="py-3 px-4 font-medium">{month.month_name}</td>
                              <td className="py-3 px-4 text-right font-mono">
                                {month.revenue.rooms_sold.toLocaleString()}
                              </td>
                              <td className="py-3 px-4 text-right font-mono">
                                {month.revenue.occupancy_rate.toFixed(1)}%
                              </td>
                              <td className="py-3 px-4 text-right font-mono">
                                ${month.revenue.adr.toFixed(0)}
                              </td>
                              <td className="py-3 px-4 text-right font-mono text-emerald-600">
                                {formatCurrency(month.total_revenue)}
                              </td>
                              <td className="py-3 px-4 text-right font-mono text-rose-600">
                                {formatCurrency(month.total_expenses)}
                              </td>
                              <td
                                className={`py-3 px-4 text-right font-mono font-semibold ${
                                  month.operating_profit >= 0
                                    ? "text-emerald-600"
                                    : "text-rose-600"
                                }`}
                              >
                                {formatCurrency(month.operating_profit)}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Badge
                                  className={`${
                                    margin >= 15
                                      ? "bg-emerald-100 text-emerald-700"
                                      : margin >= 0
                                      ? "bg-amber-100 text-amber-700"
                                      : "bg-rose-100 text-rose-700"
                                  }`}
                                >
                                  {margin.toFixed(1)}%
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-semibold">
                          <td className="py-3 px-4">Annual Total</td>
                          <td className="py-3 px-4 text-right font-mono">
                            {summary?.total_rooms_sold?.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-right font-mono">
                            {summary?.average_occupancy?.toFixed(1)}% avg
                          </td>
                          <td className="py-3 px-4 text-right font-mono">
                            ${summary?.average_adr?.toFixed(0)} avg
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-emerald-600">
                            {formatCurrency(summary?.total_revenue)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-rose-600">
                            {formatCurrency(summary?.total_expenses)}
                          </td>
                          <td
                            className={`py-3 px-4 text-right font-mono ${
                              (summary?.operating_profit || 0) >= 0
                                ? "text-emerald-600"
                                : "text-rose-600"
                            }`}
                          >
                            {formatCurrency(summary?.operating_profit)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono">
                            {(
                              ((summary?.operating_profit || 0) / (summary?.total_revenue || 1)) *
                              100
                            ).toFixed(1)}
                            %
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-6">
            {/* Cash Flow Chart */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Monthly Cash Flow - FY {currentYearData?.year}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cashFlowData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #E2E8F0",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="inflow" fill="#059669" name="Cash Inflow" />
                      <Bar dataKey="outflow" fill="#E11D48" name="Cash Outflow" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Cash Flow Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <p className="text-sm font-medium text-slate-500">Opening Balance</p>
                  <p className="text-xl font-bold text-slate-900 mt-1 font-mono">
                    {formatCurrency(summary?.cash_flow?.opening_balance || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <p className="text-sm font-medium text-slate-500">Total Inflow</p>
                  <p className="text-xl font-bold text-emerald-600 mt-1 font-mono">
                    {formatCurrency(summary?.cash_flow?.total_inflow || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <p className="text-sm font-medium text-slate-500">Total Outflow</p>
                  <p className="text-xl font-bold text-rose-600 mt-1 font-mono">
                    {formatCurrency(summary?.cash_flow?.total_outflow || 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <p className="text-sm font-medium text-slate-500">Closing Balance</p>
                  <p
                    className={`text-xl font-bold mt-1 font-mono ${
                      (summary?.cash_flow?.closing_balance || 0) >= 0
                        ? "text-emerald-600"
                        : "text-rose-600"
                    }`}
                  >
                    {formatCurrency(summary?.cash_flow?.closing_balance || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Cash Flow Table */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Cash Flow Statement - FY {currentYearData?.year}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <div className="min-w-[700px]">
                    <table className="w-full budget-table text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 bg-slate-50">Month</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Cash Sales</th>
                          <th className="text-right py-3 px-4 bg-slate-50">AR Collection</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Total Inflow</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Total Outflow</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Net Movement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentYearData?.months.map((month, index) => (
                          <tr
                            key={index}
                            className="border-b border-slate-100 hover:bg-slate-50"
                          >
                            <td className="py-3 px-4 font-medium">{month.month_name}</td>
                            <td className="py-3 px-4 text-right font-mono">
                              {formatCurrency(month.cash_flow.cash_inflow)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono">
                              {formatCurrency(month.cash_flow.ar_collection)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-emerald-600">
                              {formatCurrency(month.cash_flow.total_inflow)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-rose-600">
                              {formatCurrency(month.cash_flow.total_outflow)}
                            </td>
                            <td
                              className={`py-3 px-4 text-right font-mono font-semibold ${
                                month.cash_flow.net_movement >= 0
                                  ? "text-emerald-600"
                                  : "text-rose-600"
                              }`}
                            >
                              {formatCurrency(month.cash_flow.net_movement)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scenarios Tab */}
          <TabsContent value="scenarios" className="space-y-6">
            {scenarioData ? (
              <>
                {/* Scenario Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {scenarioData.scenarios?.map((scenario, idx) => {
                    const defaultColors = {
                      conservative: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "bg-amber-100" },
                      base: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: "bg-slate-100" },
                      optimistic: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "bg-emerald-100" },
                    };
                    const customColors = [
                      { bg: "bg-[#F3F5F2]", border: "border-[#C8D4C0]", text: "text-[#3C4C33]", icon: "bg-[#E8EDE5]" },
                      { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: "bg-blue-100" },
                      { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "bg-rose-100" },
                    ];
                    
                    const isCustom = scenario.is_custom || !['conservative', 'base', 'optimistic'].includes(scenario.scenario_name);
                    const customIndex = isCustom ? scenarioData.scenarios?.filter((s, i) => i < idx && (s.is_custom || !['conservative', 'base', 'optimistic'].includes(s.scenario_name))).length : 0;
                    const color = isCustom 
                      ? customColors[customIndex % customColors.length]
                      : (defaultColors[scenario.scenario_name] || defaultColors.base);
                    const firstYear = scenario.yearly_data?.[0] || {};
                    
                    return (
                      <Card key={idx} className={`${color.border} ${color.bg}`} data-testid={`scenario-card-${scenario.scenario_name}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className={`text-lg ${color.text}`}>
                              {scenario.scenario_label}
                            </CardTitle>
                            {isCustom && (
                              <Badge className="bg-[#E8EDE5] text-[#3C4C33] text-xs">Custom</Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            Occ: {scenario.adjustments.occupancy > 0 ? '+' : ''}{scenario.adjustments.occupancy}% | 
                            ADR: {scenario.adjustments.adr > 0 ? '+' : ''}{scenario.adjustments.adr}% | 
                            Exp: {scenario.adjustments.expenses > 0 ? '+' : ''}{scenario.adjustments.expenses}%
                          </p>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-slate-500">Year 1 Revenue</p>
                              <p className="text-xl font-bold font-mono">{formatCurrency(firstYear.total_revenue || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Year 1 Profit</p>
                              <p className={`text-xl font-bold font-mono ${(firstYear.operating_profit || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {formatCurrency(firstYear.operating_profit || 0)}
                              </p>
                            </div>
                            <div className="pt-2 border-t border-slate-200">
                              <p className="text-xs text-slate-500">5-Year Total Profit</p>
                              <p className={`text-lg font-bold font-mono ${(scenario.five_year_summary?.total_profit || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {formatCurrency(scenario.five_year_summary?.total_profit || 0)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Scenario Comparison Chart */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle>5-Year Revenue Comparison by Scenario</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={scenarioData.scenarios?.[0]?.yearly_data?.map((_, yearIdx) => {
                            const yearData = { year: `FY${scenarioData.fiscal_year_start + yearIdx}` };
                            scenarioData.scenarios?.forEach(s => {
                              yearData[s.scenario_label] = s.yearly_data?.[yearIdx]?.total_revenue || 0;
                            });
                            return yearData;
                          }) || []}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          {scenarioData.scenarios?.map((s, idx) => {
                            const chartColors = ["#F59E0B", "#64748B", "#059669", "#8B5CF6", "#3B82F6", "#EC4899"];
                            return (
                              <Bar key={idx} dataKey={s.scenario_label} fill={chartColors[idx % chartColors.length]} />
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Scenario Comparison Table */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle>Detailed Scenario Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="w-full">
                      <div className="min-w-[800px]">
                        <table className="w-full budget-table text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 bg-slate-50">Metric</th>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <th key={idx} className="text-right py-3 px-4 bg-slate-50">{s.scenario_label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4 font-medium">5-Year Total Revenue</td>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <td key={idx} className="py-3 px-4 text-right font-mono">
                                  {formatCurrency(s.five_year_summary?.total_revenue || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4 font-medium">5-Year Total Profit</td>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <td key={idx} className={`py-3 px-4 text-right font-mono ${(s.five_year_summary?.total_profit || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {formatCurrency(s.five_year_summary?.total_profit || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4 font-medium">Avg Profit Margin</td>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <td key={idx} className="py-3 px-4 text-right font-mono">
                                  {s.five_year_summary?.average_margin || 0}%
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-slate-100 bg-slate-50">
                              <td className="py-3 px-4 font-semibold" colSpan={scenarioData.scenarios?.length + 1}>
                                Year 1 Breakdown
                              </td>
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4">  Revenue</td>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <td key={idx} className="py-3 px-4 text-right font-mono">
                                  {formatCurrency(s.yearly_data?.[0]?.total_revenue || 0)}
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4">  Occupancy</td>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <td key={idx} className="py-3 px-4 text-right font-mono">
                                  {s.yearly_data?.[0]?.average_occupancy || 0}%
                                </td>
                              ))}
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4">  RevPAR</td>
                              {scenarioData.scenarios?.map((s, idx) => (
                                <td key={idx} className="py-3 px-4 text-right font-mono">
                                  {formatCurrency(s.yearly_data?.[0]?.revpar || 0)}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Strategic Insights - Dynamic based on scenarios */}
                <Card className="border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
                      Scenario Insights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {scenarioData.scenarios?.slice(0, 6).map((scenario, idx) => {
                        const isPositive = (scenario.five_year_summary?.total_profit || 0) > 0;
                        const isCustom = scenario.is_custom || !['conservative', 'base', 'optimistic'].includes(scenario.scenario_name);
                        const iconColors = ['text-amber-400', 'text-slate-300', 'text-emerald-400', 'text-[#8FA385]', 'text-blue-400', 'text-rose-400'];
                        
                        return (
                          <div key={idx} className="p-4 bg-white/10 rounded-lg">
                            <div className="flex items-center gap-2 mb-2" style={{ color: iconColors[idx % iconColors.length] }}>
                              {isPositive ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                              <span className="font-medium">{scenario.scenario_label}</span>
                              {isCustom && <Badge className="bg-[#5A7A5A]/30 text-[#C8D4C0] text-xs ml-1">Custom</Badge>}
                            </div>
                            <p className="text-slate-300 text-sm">
                              Projects {formatCurrency(scenario.five_year_summary?.total_profit || 0)} profit over 5 years with {scenario.five_year_summary?.average_margin || 0}% margin.
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-transparent rounded-full spinner" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Break-Even Tab */}
          <TabsContent value="breakeven" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-600" />
                    Break-Even Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Fixed Costs (Annual)</p>
                        <p className="text-lg font-bold text-slate-900 font-mono">
                          {formatCurrency(summary?.break_even?.fixed_costs || 0)}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Variable Cost Ratio</p>
                        <p className="text-lg font-bold text-slate-900 font-mono">
                          {summary?.break_even?.variable_cost_ratio || 0}%
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-slate-500">Contribution Margin</p>
                        <p className="text-lg font-bold text-emerald-600 font-mono">
                          {summary?.break_even?.contribution_margin || 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Break-Even Point</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl text-white">
                    <p className="text-sm text-slate-300 mb-2">Break-Even Revenue</p>
                    <p
                      className="text-3xl font-bold"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {formatCurrency(summary?.break_even?.break_even_revenue || 0)}
                    </p>
                  </div>
                  <div className="text-center p-6 bg-amber-50 rounded-xl">
                    <p className="text-sm text-amber-700 mb-2">Required Occupancy</p>
                    <p
                      className="text-3xl font-bold text-amber-600"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {summary?.break_even?.break_even_occupancy || 0}%
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      Current: {summary?.average_occupancy || 0}%
                    </p>
                  </div>
                  {(summary?.average_occupancy || 0) > (summary?.break_even?.break_even_occupancy || 0) ? (
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-medium">Above Break-Even</span>
                      </div>
                      <p className="text-sm text-emerald-600 mt-1">
                        Operating {((summary?.average_occupancy || 0) - (summary?.break_even?.break_even_occupancy || 0)).toFixed(1)}% above break-even occupancy
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                      <div className="flex items-center gap-2 text-rose-700">
                        <TrendingDown className="w-5 h-5" />
                        <span className="font-medium">Below Break-Even</span>
                      </div>
                      <p className="text-sm text-rose-600 mt-1">
                        Need {((summary?.break_even?.break_even_occupancy || 0) - (summary?.average_occupancy || 0)).toFixed(1)}% more occupancy to break even
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* 5-Year Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>5-Year Revenue & Profit Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={yearlyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #E2E8F0",
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#059669"
                        strokeWidth={2}
                        dot={{ fill: "#059669" }}
                        name="Total Revenue"
                      />
                      <Line
                        type="monotone"
                        dataKey="profit"
                        stroke="#B45309"
                        strokeWidth={2}
                        dot={{ fill: "#B45309" }}
                        name="Operating Profit"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* 5-Year Summary Table */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>5-Year Financial Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <div className="min-w-[600px]">
                    <table className="w-full budget-table text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 bg-slate-50">Year</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Revenue</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Expenses</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Profit</th>
                          <th className="text-right py-3 px-4 bg-slate-50">Occupancy</th>
                          <th className="text-right py-3 px-4 bg-slate-50">ADR</th>
                          <th className="text-right py-3 px-4 bg-slate-50">RevPAR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projections.map((year, index) => (
                          <tr
                            key={index}
                            className={`border-b border-slate-100 hover:bg-slate-50 ${
                              index === selectedYear ? "bg-amber-50" : ""
                            }`}
                          >
                            <td className="py-3 px-4 font-medium">FY {year.year}</td>
                            <td className="py-3 px-4 text-right font-mono text-emerald-600">
                              {formatCurrency(year.annual_summary.total_revenue)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-rose-600">
                              {formatCurrency(year.annual_summary.total_expenses)}
                            </td>
                            <td
                              className={`py-3 px-4 text-right font-mono font-semibold ${
                                year.annual_summary.operating_profit >= 0
                                  ? "text-emerald-600"
                                  : "text-rose-600"
                              }`}
                            >
                              {formatCurrency(year.annual_summary.operating_profit)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono">
                              {year.annual_summary.average_occupancy}%
                            </td>
                            <td className="py-3 px-4 text-right font-mono">
                              ${Number(year.annual_summary.average_adr).toFixed(2)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono">
                              ${Number(year.annual_summary.revpar).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
}
